"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ChevronLeft, ArrowRight, TrendingUp } from "lucide-react";
import Link from "next/link";

export default function PromoterHero() {
  const { t } = useLang();

  // Animated counters
  const [tickets, setTickets] = useState(0);
  const [revenue, setRevenue] = useState(0);
  const [commission, setCommission] = useState(0);

  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const dur = 4500;
    const targetT = 87;
    const targetR = 3480;
    const targetC = 522;
    const tick = (now: number) => {
      const tt = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - tt, 3);
      setTickets(Math.round(targetT * eased));
      setRevenue(Math.round(targetR * eased));
      setCommission(Math.round(targetC * eased));
      if (tt < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-[#0A0814] text-white">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-promoter-portal.mp4" type="video/mp4" />
      </video>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0A0814]/75 pointer-events-none" />
      <div
        className="absolute top-1/4 -left-32 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 -right-32 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.20) 0%, transparent 60%)" }}
      />
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/55 hover:text-white transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#F472B6" }}
        >
          {t("Promoter links · Commissions calculated", "Links de promotores · Comisiones calculadas")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[36px] sm:text-[52px] md:text-[72px] lg:text-[88px] font-black leading-[1.0] tracking-[-0.04em] mb-6"
        >
          {t("Promoter sells.", "Promotor vende.")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 50%, #FB923C 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("You see every sale.", "Tú ves cada venta.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-white/60 max-w-[680px] mx-auto leading-relaxed mb-12"
        >
          {t(
            "Each promoter gets their own tracked link for every event. Every ticket sold through it is attributed to them, and Redy calculates the commission — flat per ticket or a percentage. You see it live, pay them your way, and mark it paid. No Sunday text fights over who sold what.",
            "Cada promotor tiene su propio link rastreado para cada evento. Cada entrada vendida por ahí queda a su nombre y Redy calcula la comisión — fija por entrada o porcentaje. Lo ves en vivo, les pagas como prefieras y lo marcas pagado. Sin pelearse por SMS el domingo por quién vendió qué."
          )}
        </motion.p>

        {/* Promoter dashboard mockup */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-6 md:p-8 max-w-[920px] mx-auto mb-12"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-[14px]"
                style={{
                  background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
                }}
              >
                S
              </div>
              <div>
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                  {t("Sales by promoter", "Ventas por promotor")}
                </div>
                <div className="text-[16px] font-black text-white">Sofia Reyes</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#F472B6" }} />
              <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#F472B6" }}>
                Live
              </span>
            </div>
          </div>

          {/* 3 odometers */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Tickets sold */}
            <div className="bg-white/[0.03] border border-white/5 rounded-[16px] p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-2">
                {t("Tickets sold", "Tickets vendidos")}
              </div>
              <div className="text-[32px] font-black text-white tabular-nums leading-none">
                {tickets}
              </div>
              <div className="text-[10px] font-bold mt-1" style={{ color: "#C4B5FD" }}>
                {t("Friday · NEON RUSH", "Viernes · NEON RUSH")}
              </div>
            </div>

            {/* Revenue */}
            <div className="bg-white/[0.03] border border-white/5 rounded-[16px] p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-white/45 mb-2">
                {t("Revenue brought", "Revenue generado")}
              </div>
              <div
                className="text-[32px] font-black tabular-nums leading-none"
                style={{
                  background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                ${revenue.toLocaleString()}
              </div>
              <div className="text-[10px] font-bold mt-1" style={{ color: "#C4B5FD" }}>
                {t("To the venue", "Para el venue")}
              </div>
            </div>

            {/* Commission */}
            <div
              className="rounded-[16px] p-4 border"
              style={{
                backgroundColor: "rgba(244,114,182,0.10)",
                borderColor: "rgba(244,114,182,0.30)",
              }}
            >
              <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "#F472B6" }}>
                {t("Her commission", "Su comisión")}
              </div>
              <div className="text-[32px] font-black text-white tabular-nums leading-none">
                ${commission}
              </div>
              <div className="text-[10px] font-bold mt-1" style={{ color: "#F472B6" }}>
                {t("15% · calculated", "15% · calculada")}
              </div>
            </div>
          </div>

          {/* Recent sales feed */}
          <div className="space-y-1.5" style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-2">
              {t("Recent (live)", "Recientes (en vivo)")}
            </div>
            {[
              { name: "Carlos R.", tier: "GA", amount: "$40", time: "11:34 PM" },
              { name: "Daniela L.", tier: "VIP", amount: "$160", time: "11:31 PM" },
              { name: "Marco A.", tier: "GA+1", amount: "$80", time: "11:28 PM" },
            ].map((sale, i) => (
              <motion.div
                key={`${sale.name}-${i}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: 0.9 + 0.1 * i }}
                className="flex items-center gap-3 bg-white/[0.03] rounded-[10px] px-3 py-2 text-[11px]"
              >
                <div className="text-white/45 tabular-nums">{sale.time}</div>
                <div className="text-white font-bold flex-1">{sale.name}</div>
                <div
                  className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: sale.tier.startsWith("VIP") ? "rgba(244,114,182,0.18)" : "rgba(255,255,255,0.06)",
                    color: sale.tier.startsWith("VIP") ? "#F472B6" : "rgba(255,255,255,0.55)",
                  }}
                >
                  {sale.tier}
                </div>
                <div className="font-black tabular-nums" style={{ color: "#C4B5FD" }}>
                  {sale.amount}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center"
        >
          <Link
            href="/signup?vertical=nightlife"
            className="group inline-flex items-center gap-2 text-[15px] font-bold px-8 py-4 rounded-full hover:scale-[1.03] transition-transform shadow-2xl text-white"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
              boxShadow: "0 20px 60px rgba(124, 58, 237, 0.5)",
            }}
          >
            {t("End the Sunday text fights →", "Termina las peleas del domingo →")}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
